import { useToastContext } from '@rtkwlf/fenrir-react';
import type {
  ManagementUpdate,
  ResponseError,
  RiskUpdateResult,
} from '@rtkwlf/mr-infra-risk';
import {
  useMutation,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { useOrganizationContext } from '@packages/legacy/organization-context';
import { useFeatureFlag } from '@packages/platform/feature-flags/use-feature-flag';

import { useRiskServiceApi } from '../use-mr-infra-risk-api';

export type UpdateRisks = {
  selectedRisks: string[];
  update: ManagementUpdate;
};

type Options = Omit<
  UseMutationOptions<RiskUpdateResult, ResponseError, UpdateRisks>,
  'mutationFn'
>;

export const NULLABLE_TIME = '0001-01-01T00:00:00Z';
export const NULLABLE_ID = '00000000-0000-0000-0000-000000000000';

export const useUpdateRisks = (options?: Options) => {
  const { addToast } = useToastContext();
  const { t } = useTranslation('riskManagement', {
    keyPrefix: 'risks.notifications',
  });
  const queryClient = useQueryClient();
  const api = useRiskServiceApi();
  const { id } = useOrganizationContext().selectedOrganization;
  const [testDataFF] = useFeatureFlag('RISK_IN_UP_LIVE_TEST_DATA');

  const orgUUID = testDataFF ? 'c8923d83-d419-4453-b768-3e1fbd6d16c2' : id;

  return useMutation({
    mutationFn: ({ selectedRisks, update }: UpdateRisks) =>
      api.updateRisks({
        orgUUID,
        body: {
          update: {
            management: update,
          },
          affects: {
            identifiers: selectedRisks,
          },
        },
      }),
    onMutate: async (_variables) => {
      await queryClient.cancelQueries({
        queryKey: [orgUUID, 'mr-infra-risk', 'risks'],
        exact: false,
      });
      return {};
    },
    onSuccess: (_data, _variables) => {
      queryClient.invalidateQueries({
        queryKey: [orgUUID, 'mr-infra-risk', 'risks'],
        exact: false,
      });
      addToast({
        // ToDo : Convert this to Fenrir V2 Toast once it is available.
        // Jira: https://arcticwolf.atlassian.net/browse/MR-6001
        appearance: 'success',
        duration: 5,
        message: t('updateSuccess'),
        title: t('requestSucceeded'),
      });
    },

    onError: (error) => {
      addToast({
        // ToDo : Convert this to Fenrir V2 Toast once it is available.
        // Jira: https://arcticwolf.atlassian.net/browse/MR-6001
        appearance: 'danger',
        duration: 5,
        message: t('error'),
        title: t('updateError'),
      });
    },
    ...options,
  });
};
